/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
import * as React from 'react';
import cx from 'classnames';
import { useTheme, useOverflow, useMergedRefs } from '../utils/index.js';
/**
 * Group buttons together for common actions.
 * Handles responsive overflow when the `overflowButton` prop is specified.
 *
 * @example
 * <ButtonGroup>
 *   <IconButton>
 *     <SvgAdd />
 *   </IconButton>
 *   <IconButton>
 *     <SvgEdit />
 *   </IconButton>
 * </ButtonGroup>
 *
 * @example
 * const buttons = [...Array(10)].map((_, index) => <IconButton><SvgPlaceholder /></IconButton>);
 * <ButtonGroup
 *   overflowButton={(overflowStart) => <DropdownMenu menuItems={(close) =>
 *     [...Array(buttons.length - overflowStart + 1)].map((_, index) => (
 *       <MenuItem icon={<SvgPlaceholder />} onClick={close}>Button #{overflowStart + index}</MenuItem>
 *     ))
 *   }>
 *     <IconButton><SvgMore /></IconButton>
 *   </DropdownMenu>}
 * >
 *   {buttons}
 * </ButtonGroup>
 */
export const ButtonGroup = React.forwardRef((props, ref) => {
    const { children, className, overflowButton, overflowPlacement = 'end', orientation = 'horizontal', ...rest } = props;
    const items = React.useMemo(() => {
        var _a, _b;
        return (_b = (_a = React.Children.map(children, (child) => !!child ? React.createElement("div", null, child) : undefined)) === null || _a === void 0 ? void 0 : _a.filter(Boolean)) !== null && _b !== void 0 ? _b : [];
    }, [children]);
    useTheme();
    const [overflowRef, visibleCount] = useOverflow(items, !overflowButton, orientation);
    const refs = useMergedRefs(overflowRef, ref);
    return (React.createElement("div", { className: cx({
            'iui-button-group': orientation === 'horizontal',
            'iui-button-group-vertical': orientation === 'vertical',
            'iui-button-group-overflow-x': !!overflowButton && orientation === 'horizontal',
        }, className), "aria-orientation": orientation, ref: refs, ...rest },
        React.createElement(React.Fragment, null,
            visibleCount < items.length &&
                overflowButton &&
                overflowPlacement === 'start' && (React.createElement("div", null, overflowButton(visibleCount))),
            visibleCount < items.length
                ? items.slice(0, Math.max(0, visibleCount - 1))
                : items,
            visibleCount < items.length &&
                overflowButton &&
                overflowPlacement === 'end' && (React.createElement("div", null, overflowButton(visibleCount))))));
});
export default ButtonGroup;
